<template>
  <div
    v-if="open"
    class="overlay"
    role="dialog"
    aria-modal="true"
    aria-labelledby="backsheet-title"
    @keydown.esc.stop.prevent="emitClose"
  >
    <div class="dialog">
      <header class="dialog-header">
        <h2
          id="backsheet-title"
          class="h1"
        >
          Rückblatt erfassen
          <span class="text-small font-normal">für {{ student.first_name }} {{ student.last_name }}</span>
        </h2>
        <button
          class="btn icon-only"
          type="button"
          aria-label="Schließen"
          @click="emitClose"
        >
          <i
            class="pi pi-times"
            aria-hidden="true"
          />
        </button>
      </header>

      <section
        ref="dialogBodyEl"
        class="dialog-body"
      >
        <form
          class="form"
          novalidate
          @submit.prevent="submit"
        >
          <div class="field">
            <label for="bs-activities">Zusätzliche Aktivitäten</label>
            <textarea
              id="bs-activities"
              v-model.trim="model.additional_activities"
              class="input"
              rows="3"
              required
              :aria-invalid="touched.additional_activities && !!errors.additional_activities"
              @blur="markTouched('additional_activities'); validateField('additional_activities')"
            />
            <p
              v-if="touched.additional_activities && errors.additional_activities"
              class="help danger"
              role="alert"
            >
              {{ errors.additional_activities }}
            </p>
          </div>

          <div class="field">
            <label for="bs-remarks">Bemerkungen</label>
            <textarea
              id="bs-remarks"
              v-model.trim="model.remarks"
              class="input"
              rows="3"
              required
              :aria-invalid="touched.remarks && !!errors.remarks"
              @blur="markTouched('remarks'); validateField('remarks')"
            />
            <p
              v-if="touched.remarks && errors.remarks"
              class="help danger"
              role="alert"
            >
              {{ errors.remarks }}
            </p>
          </div>

          <div class="">
            Versäumnisse
          </div>
          <div class="two-col">
            <div class="field">
              <label for="bs-absences">Fehltage</label>
              <input
                id="bs-absences"
                v-model.trim="model.absences"
                type="text"
                class="input"
                required
                inputmode="numeric"
                pattern="\\d+"
                placeholder="z. B. 3"
                :aria-invalid="touched.absences && !!errors.absences"
                @blur="markTouched('absences'); validateField('absences')"
              >
              <p
                v-if="touched.absences && errors.absences"
                class="help danger"
                role="alert"
              >
                {{ errors.absences }}
              </p>
            </div>

            <div class="field">
              <label for="bs-unexcused-absences">davon unentschuldigt</label>
              <input
                id="bs-unexcused-absences"
                v-model.trim="model.unexcused_absences"
                type="text"
                class="input"
                required
                inputmode="numeric"
                pattern="\\d+"
                placeholder="z. B. 1"
                :aria-invalid="touched.unexcused_absences && !!errors.unexcused_absences"
                @blur="markTouched('unexcused_absences'); validateField('unexcused_absences')"
              >
              <p
                v-if="touched.unexcused_absences && errors.unexcused_absences"
                class="help danger"
                role="alert"
              >
                {{ errors.unexcused_absences }}
              </p>
            </div>

            <div class="field">
              <label for="bs-learningtimes">Lernzeiten</label>
              <input
                id="bs-learningtimes"
                v-model.trim="model.learning_times"
                type="text"
                class="input"
                required
                inputmode="numeric"
                pattern="\\d+"
                placeholder="z. B. 5"
                :aria-invalid="touched.learning_times && !!errors.learning_times"
                @blur="markTouched('learning_times'); validateField('learning_times')"
              >
              <p
                v-if="touched.learning_times && errors.learning_times"
                class="help danger"
                role="alert"
              >
                {{ errors.learning_times }}
              </p>
            </div>

            <div class="field">
              <label for="bs-unexcused-learning">davon unentschuldigt</label>
              <input
                id="bs-unexcused-learning"
                v-model.trim="model.unexcused_learning_times"
                type="text"
                class="input"
                required
                inputmode="numeric"
                pattern="\\d+"
                placeholder="z. B. 0"
                :aria-invalid="touched.unexcused_learning_times && !!errors.unexcused_learning_times"
                @blur="markTouched('unexcused_learning_times'); validateField('unexcused_learning_times')"
              >
              <p
                v-if="touched.unexcused_learning_times && errors.unexcused_learning_times"
                class="help danger"
                role="alert"
              >
                {{ errors.unexcused_learning_times }}
              </p>
            </div>

            <div class="field">
              <label for="bs-tardiness">Verspätungen</label>
              <input
                id="bs-tardiness"
                v-model.trim="model.tardiness"
                type="text"
                class="input"
                required
                inputmode="numeric"
                pattern="\\d+"
                placeholder="z. B. 2"
                :aria-invalid="touched.tardiness && !!errors.tardiness"
                @blur="markTouched('tardiness'); validateField('tardiness')"
              >
              <p
                v-if="touched.tardiness && errors.tardiness"
                class="help danger"
                role="alert"
              >
                {{ errors.tardiness }}
              </p>
            </div>

            <div class="field">
              <label for="bs-date">Datum</label>
              <flat-pickr
                id="bs-date"
                v-model="model.date"
                :config="datePickerConfig"
                class="input"
                aria-label="Datum auswählen"
              />
            </div>
          </div>
        </form>

        <!-- Analytics-Panel (im scrollbaren Bereich, damit wir hinscrollen können) -->
        <div
          v-if="analyticsVisible"
          ref="analyticsEl"
          class="notice"
          style="margin: 10px 0 0 0;"
          role="status"
        >
          <template v-if="analyticsLoading">
            Lädt …
          </template>
          <template v-else-if="analyticsError">
            <span
              class="help danger"
              role="alert"
            >{{ analyticsError }}</span>
          </template>
          <template v-else>
            <strong>Überblick {{ selectedYearCode }}:</strong>
            <ul style="margin: 6px 0 0 18px;">
              <li>
                Fehltage: {{ analytics.days_absent }} (davon unentschuldigt: {{
                  analytics.days_absent_unexcused
                }})
              </li>
              <li>
                Lernzeiten: {{ analytics.learning_times }} (davon unentschuldigt:
                {{ analytics.learning_times_unexcused }})
              </li>
              <li>Verspätungen: {{ analytics.tardiness_count }}</li>
            </ul>
          </template>
        </div>
      </section>

      <footer class="dialog-footer">
        <!-- Schuljahr-Auswahl + Eye-Button (Analyse) -->
        <select
          v-model="selectedYearCode"
          class="input text-center"
          aria-label="Schuljahr auswählen"
          style="height: 36px; font-size:14px;"
          @change="onSchoolYearChange"
        >
          <option
            v-for="sy in schoolYears"
            :key="sy.code"
            :value="sy.code"
          >
            {{ sy.code }}
          </option>
        </select>
        <button
          class="btn"
          type="button"
          :disabled="analyticsLoading || !selectedYearCode"
          title="Überblick anzeigen"
          aria-label="Überblick anzeigen"
          @click="onShowAnalytics"
        >
          <i
            class="pi pi-eye"
            aria-hidden="true"
          />
        </button>

        <span style="flex:1;" />

        <button
          class="btn"
          type="button"
          @click="emitClose"
        >
          Abbrechen
        </button>
        <button
          class="btn btn-primary"
          type="button"
          :disabled="saving || !isValid"
          @click="submit"
        >
          <span
            v-if="saving"
            class="spinner"
            aria-hidden="true"
          />
          Speichern
        </button>
      </footer>
    </div>
  </div>
</template>

<script setup>
import {computed, reactive, ref, watch, nextTick} from 'vue';
import flatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';
import api from '@/services/api';

const props = defineProps({
  open: {type: Boolean, default: false},
  student: {type: Object, default: null},
  initial: {type: Object, default: null},
});

// Schuljahre-Dropdown
const schoolYears = ref([]);
const selectedYearCode = ref('');

// Analytics-State
const analyticsVisible = ref(false);
const analyticsLoading = ref(false);
const analyticsError = ref('');
const analytics = reactive({
  days_absent: 0,
  days_absent_unexcused: 0,
  learning_times: 0,
  learning_times_unexcused: 0,
  tardiness_count: 0,
});

const emit = defineEmits(['close', 'save']);

const model = reactive({
  date: '',
  additional_activities: '',
  remarks: '',
  absences: '',
  learning_times: '',
  tardiness: '',
  unexcused_absences: '',
  unexcused_learning_times: '',
});

const saving = ref(false);

watch(() => props.initial, (val) => {
  const src = val && typeof val === 'object' ? val : {};
  const todayIso = new Date().toISOString().slice(0, 10);

  model.date = String(src.date ?? '').trim() || todayIso;
  model.additional_activities = src.additional_activities ?? '';
  model.remarks = src.remarks ?? '';
  model.absences = src.absences ?? '';
  model.learning_times = src.learning_times ?? '';
  model.tardiness = src.tardiness ?? '';
  model.unexcused_absences = src.unexcused_absences ?? '';
  model.unexcused_learning_times = src.unexcused_learning_times ?? '';
}, {immediate: true});

// Beim Öffnen: Schuljahre laden (Codes, absteigend)
watch(() => props.open, async (isOpen) => {
  if (!isOpen) {
    analyticsVisible.value = false;
    analyticsError.value = '';
    analyticsLoading.value = false;
    return;
  }
  try {
    const {data} = await api.get('/school-years');
    const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
    schoolYears.value = items
        .map(it => ({code: String(it.code || '').trim(), start_date: it.start_date}))
        .filter(it => it.code);
    schoolYears.value.sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());
    if (!selectedYearCode.value && schoolYears.value.length) {
      selectedYearCode.value = schoolYears.value[0].code;
    }
  } catch (e) {
    schoolYears.value = [];
  }
}, {immediate: true});

const datePickerConfig = computed(() => ({
  altInput: true,
  altFormat: 'd.m.Y',
  dateFormat: 'Y-m-d',
  locale: German,
  allowInput: true
}));

// Validierung
const touched = reactive({
  date: false,
  additional_activities: false,
  remarks: false,
  absences: false,
  learning_times: false,
  tardiness: false,
  unexcused_absences: false,
  unexcused_learning_times: false,
});

const errors = reactive({
  date: '',
  additional_activities: '',
  remarks: '',
  absences: '',
  learning_times: '',
  tardiness: '',
  unexcused_absences: '',
  unexcused_learning_times: '',
});

function markTouched(name) {
  if (Object.prototype.hasOwnProperty.call(touched, name)) {
    touched[name] = true;
  }
}

function validateField(name) {
  const req = (v) => String(v || '').trim() !== '';
  const num = (v) => /^\d+$/.test(String(v || '').trim());
  switch (name) {
    case 'date': {
      const v = String(model.date || '').trim();
      if (!v) {
        errors.date = '';
      } else if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        errors.date = 'Ungültiges Datum. Bitte TT.MM.JJJJ auswählen.';
      } else {
        errors.date = '';
      }
      break;
    }
    case 'additional_activities':
      errors.additional_activities = req(model.additional_activities) ? '' : 'Bitte zusätzliche Aktivitäten angeben.';
      break;
    case 'remarks':
      errors.remarks = req(model.remarks) ? '' : 'Bitte Bemerkungen angeben.';
      break;
    case 'absences':
      errors.absences = req(model.absences) && num(model.absences) ? '' : 'Bitte Fehltage (Zahl) angeben.';
      break;
    case 'learning_times':
      errors.learning_times = req(model.learning_times) && num(model.learning_times) ? '' : 'Bitte Lernzeiten (Zahl) angeben.';
      break;
    case 'tardiness':
      errors.tardiness = req(model.tardiness) && num(model.tardiness) ? '' : 'Bitte Verspätungen (Zahl) angeben.';
      break;
    case 'unexcused_absences':
      errors.unexcused_absences = req(model.unexcused_absences) && num(model.unexcused_absences) ? '' : 'Bitte unentschuldigte Fehltage (Zahl) angeben.';
      break;
    case 'unexcused_learning_times':
      errors.unexcused_learning_times = req(model.unexcused_learning_times) && num(model.unexcused_learning_times) ? '' : 'Bitte unentschuldigte Lernzeiten (Zahl) angeben.';
      break;
  }
}

function validateAll(update = true) {
  const fields = Object.keys(touched);
  if (update) {
    fields.forEach(f => validateField(f));
    return Object.values(errors).every(e => !e);
  } else {
    const req = (v) => String(v || '').trim() !== '';
    const num = (v) => /^\d+$/.test(String(v || '').trim());
    const dateOk = !String(model.date || '').trim() || /^\d{4}-\d{2}-\d{2}$/.test(String(model.date || '').trim());
    return dateOk
        && req(model.additional_activities)
        && req(model.remarks)
        && req(model.absences) && num(model.absences)
        && req(model.learning_times) && num(model.learning_times)
        && req(model.tardiness) && num(model.tardiness)
        && req(model.unexcused_absences) && num(model.unexcused_absences)
        && req(model.unexcused_learning_times) && num(model.unexcused_learning_times);
  }
}

const isValid = computed(() => validateAll(false));

function firstInvalidField() {
  if (errors.date) return 'bs-date';
  if (!String(model.additional_activities || '').trim() || errors.additional_activities) return 'bs-activities';
  if (!String(model.remarks || '').trim() || errors.remarks) return 'bs-remarks';
  if (!String(model.absences || '').trim() || errors.absences) return 'bs-absences';
  if (!String(model.learning_times || '').trim() || errors.learning_times) return 'bs-learningtimes';
  if (!String(model.tardiness || '').trim() || errors.tardiness) return 'bs-tardiness';
  if (!String(model.unexcused_absences || '').trim() || errors.unexcused_absences) return 'bs-unexcused-absences';
  if (!String(model.unexcused_learning_times || '').trim() || errors.unexcused_learning_times) return 'bs-unexcused-learning';
  return null;
}

function emitClose() {
  if (saving.value) return;
  emit('close');
}

function submit() {
  if (saving.value) return;

  Object.keys(touched).forEach(k => markTouched(k));
  const ok = validateAll(true);
  if (!ok) {
    const id = firstInvalidField();
    if (id) {
      const el = document.getElementById(id);
      if (el && typeof el.focus === 'function') {
        el.focus();
      }
    }
    return;
  }

  saving.value = true;
  try {
    emit('save', {
      studentId: props.student?.id ?? null,
      data: {...model}
    });
  } finally {
    saving.value = false;
  }
}

function onSchoolYearChange() {
  analyticsVisible.value = false;
  analyticsError.value = '';
  analyticsLoading.value = false;
}

const analyticsEl = ref(null);
const dialogBodyEl = ref(null);

function scrollNoticeIntoView() {
  const container = dialogBodyEl.value;
  const target = analyticsEl.value;
  if (!container || !target) return;

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const delta = (targetRect.bottom - containerRect.bottom) + 12; // kleiner Abstand
  if (delta > 0) {
    container.scrollTo({
      top: container.scrollTop + delta,
      behavior: 'smooth',
    });
  }
}

watch(
    () => analyticsVisible.value,
    async (v) => {
      if (!v) return;

      await nextTick();
      requestAnimationFrame(() => {
        scrollNoticeIntoView();
      });
    },
    {flush: 'post'}
);

async function onShowAnalytics() {
  analyticsVisible.value = true;
  analyticsError.value = '';
  analyticsLoading.value = true;

  try {
    const sid = Number(props.student?.id) || 0;
    if (!sid || !selectedYearCode.value) {
      analyticsError.value = 'Kein Schüler oder Schuljahr gewählt.';
      return;
    }
    const {data} = await api.get('/reports/backsheet-summary', {
      params: {studentId: sid, schoolYearCode: selectedYearCode.value}
    });
    analytics.days_absent = Number(data?.days_absent) || 0;
    analytics.days_absent_unexcused = Number(data?.days_absent_unexcused) || 0;
    analytics.learning_times = Number(data?.learning_times) || 0;
    analytics.learning_times_unexcused = Number(data?.learning_times_unexcused) || 0;
    analytics.tardiness_count = Number(data?.tardiness_count) || 0;
  } catch (e) {
    analyticsError.value = e?.response?.data?.error?.message || 'Überblick konnte nicht geladen werden.';
  } finally {
    analyticsLoading.value = false;
  }
}


</script>

<style scoped>
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .35);
  display: grid;
  place-items: center;
  z-index: 1000;
  padding: 16px;
}

.dialog {
  width: 100%;
  max-width: 760px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, .18);
  display: flex;
  flex-direction: column;
  height: 610px;
  overflow-y: scroll;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
}

.h1 {
  font-size: 20px;
  line-height: 24px;
  font-weight: 600;
  margin: 0;
}

.dialog-body {
  padding: 14px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px 14px 14px;
  border-top: 1px solid var(--border);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 5px;
}

.field label {
  margin-bottom: 0;
}

.input {
  height: 36px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
}

textarea.input {
  height: auto;
  min-height: 70px;
  resize: vertical;
}

.btn {
  height: 36px;
  padding: 0 12px;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 6px;
  cursor: pointer;
}

.btn-primary {
  border: 1px solid var(--primary);
  background: var(--primary);
  color: #fff;
}

.btn.icon-only {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #fff;
  border-top-color: transparent;
  border-radius: 999px;
  margin-right: 6px;
  animation: spin .9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.meta {
  color: var(--muted);
  font-size: 12px;
}

.help {
  font-size: 12px;
  line-height: 16px;
  color: var(--muted);
  margin-top: 4px;
}

.help.danger {
  color: var(--danger);
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.notice {
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 10px 12px;
  background: var(--surface);
  font-size: 14px;
}
</style>
